import type { AxiosRequestConfig, Method } from 'axios';
import { AxiosError, CanceledError } from 'axios';

import { useBrokerStore } from '@/features/Broker/store';
import { ROLE, useClientStore } from '@/features/Clients';
import { clientMappings } from '@/features/Clients/ClientMapper/clientMapper';
import { useGlobalStore } from '@/features/Global/store';
import BackendValidationError, {
  generateErrorFieldPath,
} from '@/features/validation/backendValidation';
import { VALIDATION_MAP_KEYS } from '@/features/validation/validationMapKeys';
import type {
  BaseApiResponse,
  HttpClientResponse,
  IHttpRequestConfig,
  JSendValidationContractErrorResponse,
  JSendValidationErrorResponse,
} from '@/services/api/HttpClient';
import {
  isJSendValidationContractErrorResponse,
  isJSendValidationErrorResponse,
} from '@/services/api/HttpClient';
import { HTTP_STATUS_CODE } from '@/services/api/HttpStatusCodes';
import { toAxiosErrorLogEntry } from '@/services/api/clients/axiosErrorLogEntry';
import { HttpAxiosBaseClient } from '@/services/api/clients/HttpAxiosBaseClient';
import { SessionError } from '@/services/errorHandler/SessionError';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { getErrorPath, useValidation } from '@/utils/form';
import { useLocalStorage } from '@/utils/useLocalStorage/useLocalStorage';
import {
  SESSION_STATUS,
  useSessionModal,
} from '@/utils/useSessionModal/useSessionModal';
import type { UseLogger } from '@/services/logger/useLogger';
import { useCurrentStep } from '@/utils/useCurrentRoute/useCurrentStep';
import { isJSendError } from '@/utils/jSend/types';
import { JSendError } from '@/utils/jSend/jSendError';

export class HttpAxiosClient extends HttpAxiosBaseClient {
  constructor(
    logger: UseLogger,
    bedarfId: string | undefined,
    token: string | undefined,
  ) {
    const config: AxiosRequestConfig = {
      baseURL: import.meta.env.VITE_APP_API_PATH,
      params: {},
      // Browser/OS-TCP-Timeout greift sonst bei ~60s und triggert ERR_NETWORK,
      // obwohl langlaufende Anbieter-Anträge (z.B. Concordia-RS ~63s, vereinzelt darüber) noch laufen.
      timeout: 180_000,
    };

    if (!import.meta.env.PROD) {
      config.baseURL = `${import.meta.env.VITE_APP_DEV_BASE_URL}${
        config.baseURL ?? ''
      }`;
      config.withCredentials = true;
    }

    super(config, logger, bedarfId, token);
  }

  protected doRequest<T>(
    httpMethod: Method,
    url: string,
    config?: IHttpRequestConfig,
  ): HttpClientResponse<T> {
    const abortController = new AbortController();
    const sparte = useGlobalStore().getSparte;

    const requestConfig: AxiosRequestConfig = this.buildAxiosConfig(
      config ?? {},
    );

    const request = this.axios.request<BaseApiResponse<T>>({
      ...requestConfig,
      method: httpMethod,
      url: config?.dontUseBaseUrl === true ? url : `${sparte}/${url}`,
      signal: abortController.signal,
    });

    // work around for client search as it's response structure is different from everything else...
    if (config?.noJSendFormat === true) {
      return [
        () => abortController.abort(),
        request.then((result) => result.data as unknown as T),
      ];
    }

    return [
      () => abortController.abort(),
      request.then((result) => {
        // catch cancelled results
        if (result === undefined) {
          return Promise.reject(new CanceledError());
        }

        if (result.data?.status === 'error') {
          return Promise.reject(
            new AxiosError(result.data?.message ?? 'jSend request error'),
          );
        }

        return result.data.data;
      }),
    ];
  }

  protected onError(error: AxiosError): void {
    if (
      (error.response === undefined ||
        (error.response.status !== HTTP_STATUS_CODE.NOT_FOUND &&
          error.response.status !== HTTP_STATUS_CODE.UNAUTHORIZED)) &&
      error.message !== 'canceled' &&
      !isJSendError(error.response?.data)
    ) {
      this.logger.error(toAxiosErrorLogEntry(error));
    }

    if (error?.response?.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
      const sparte = useGlobalStore().getSparte;
      const broker = useBrokerStore().broker.data;
      if (broker?.email !== undefined || broker?.id !== undefined) {
        if (useLocalStorage().isLocalStorageAvailable.value) {
          useSessionModal().open(SESSION_STATUS.ENDED);
        } else {
          /**
           * In case the session storage is not writable, we throw a
           * SessionError to trigger the refreshSession handling.
           * The input data won`t be saved at all - maybe we will find
           * a solution to store this data elsewhere in those edge-cases.
           */
          throw new SessionError(sparte);
        }
      } else {
        throw new SessionError(sparte);
      }
    } else if (
      error?.response?.status === HTTP_STATUS_CODE.BAD_REQUEST &&
      (isJSendValidationErrorResponse(error?.response?.data) ||
        isJSendValidationContractErrorResponse(error?.response?.data))
    ) {
      this.handleValidationErrorResponse(error?.response.data);
    } else if (isJSendError(error.response?.data)) {
      this.logger.error(
        'jSend error response inbound',
        toAxiosErrorLogEntry(error),
      );

      if (error.response.data.data?.identifier === 'KFZ_SETUP') {
        throw new VglError(VGL_ERROR_CODE.KFZ_SETUP, {
          userModalMessage: error.response.data.message,
          userModalTitle: 'Jetzt Daten nachreichen',
          cause: error,
        });
      }

      throw new JSendError(error.response.data.message, {
        cause: error,
        data: error.response?.data.data,
      });
    } else if (error.message !== 'canceled') {
      throw new VglError(VGL_ERROR_CODE.API_AXIOS_ERROR, {
        additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
        cause: error,
        additionalData: error?.response?.data,
      });
    }
  }

  protected handleValidationErrorResponse(
    res: JSendValidationErrorResponse | JSendValidationContractErrorResponse,
  ): void {
    // We have different responses for those two pages, therefore we need some type checks
    const isOfferResponse = isJSendValidationErrorResponse(res);
    const validationErrors = isOfferResponse ? res.data.validation : res.data;

    const { errors } = useValidation(
      useCurrentStep().step.value === 'offers'
        ? VALIDATION_MAP_KEYS.INPUT
        : VALIDATION_MAP_KEYS.CONTRACT,
    );

    const backendErrors: Record<string, string[]> = {};

    const unrelatableErrors: string[] = [];

    Object.entries(validationErrors).forEach(([errorPath, message]) => {
      let paths = errorPath
        .replaceAll('][', '::')
        .replaceAll('[', '')
        .replaceAll(']', '')
        .split('::');

      let [errorType] = paths;

      // check if errorType belongs to a mapped client field
      // this could be the case for print responses where we don`t have the same structure as for contract requests e.g.
      if (
        !['eingabedaten', 'antragsdaten', 'clientDaten'].includes(errorType)
      ) {
        const fieldMapping = clientMappings[ROLE.VERSICHERUNGSNEHMER];
        if (fieldMapping !== undefined && fieldMapping[errorType]) {
          const mappedFieldName = fieldMapping[errorType];
          errorType = 'clientDaten';
          paths = ['clientDaten', ROLE.VERSICHERUNGSNEHMER, mappedFieldName];
        }
      }

      if (errorType === 'eingabedaten' || errorType === 'antragsdaten') {
        backendErrors[generateErrorFieldPath(paths)] = [message];
      } else if (errorType === 'entities') {
        const [, clientIdString] = paths;

        const deepFieldPath = paths.slice(3).join('.'); // make sure we concat deep structure (e.g. ['adresse', 'ort'] -> adresse.ort)

        const client = useClientStore().getClientByRefId(
          Number(clientIdString),
        );

        client.roles.forEach((role) => {
          backendErrors[getErrorPath(deepFieldPath, role)] = [message];
        });
      } else if (errorType === 'clientDaten') {
        const [, roleString, fieldPath] = paths;

        const fieldMapping = clientMappings[roleString as ROLE];
        let targetPath = fieldPath;
        // check if we need to map the field path
        if (fieldMapping !== undefined && fieldMapping[fieldPath]) {
          targetPath = fieldMapping[fieldPath];
        }

        backendErrors[getErrorPath(targetPath, roleString as ROLE)] = [message];
      } else {
        unrelatableErrors.push(`${errorPath}: ${message}`);
      }
    });

    errors.value = {
      ...errors.value,
      ...backendErrors,
    };

    if (
      Object.entries(errors.value).length === 0 &&
      unrelatableErrors.length > 0
    ) {
      throw new BackendValidationError(
        `Es sind unbekannte Fehler aufgetreten: <br> ${unrelatableErrors.join(
          '<br>',
        )}`,
      );
    }

    throw new BackendValidationError(
      `backend validation failed - ${JSON.stringify(backendErrors)}`,
      {
        cause: backendErrors,
      },
    );
  }
}
